<style lang="scss" scoped>
@use "assets/scss/components/input.scss";
@use "assets/scss/variables";
.isax {
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover {
    color: variables.$red;
  }
}
* {
  z-index: 5
}
.input {
  z-index: 6;
}
</style>

<template>
  <UModal v-model:open="isOpen">
    <template #content>
      <div class="p-8 grid gap-6" v-if="loading">
        <div class="flex justify-center items-center flex-col">
          <VsLoading color="primary" type="corners" style="position: relative" />
          <p class="p-4 text-center text-primary">{{ content || $t("modal.loading") }}</p>
        </div>
      </div>
      <div class="p-8 grid gap-6" v-else style="z-index: 9999">
        <div>
          <div class="flex justify-end">
            <i class="isax isax-close-circle" @click="close" />
          </div>
          <h3>{{ title }}</h3>
          <p class="little-text pt-2">{{ content }}</p>
        </div>
        <div class="input" v-if="input && (input.type === 'text' || input.type === 'email' || input.type === 'password' || input.type === 'number')">
          <CInput v-model="input.value" :label="input.label" :type="input.type" ref="modalInput" />
        </div>
        <div class="input" v-else-if="input && input.type === 'select'">
          <USelectMenu v-model="input.value" value-key="value" :items="input.options" class="w-full" ref="modalInput" />
        </div>
        <div v-else-if="html">
          <div v-html="html" />
        </div>
        <div v-if="pdf">
          <!-- PDF is a base64 -->
          <iframe :src="'data:application/pdf;base64,'+pdf" width="100%" height="500px" />
        </div>
        <div class="flex justify-end">
          <div class="grid grid-cols-2 gap-4">
            <VsButton class="button__primary h-fit justify-center border border-gray-300" color="transparant"
                      @click="cancel" v-if="!hideCancelButton">
              <p class="p-1 text-sm text-gray-800">{{ cancelButtonText }}</p>
            </VsButton>
            <div v-else></div>
            <VsButton class="button__primary h-fit" :color="confirmButtonColor" @click="confirm"
                      :loading="loadings.includes('confirm')">
              <p class="p-1 text-sm">{{ confirmButtonText }}</p>
            </VsButton>
          </div>
        </div>
      </div>
    </template>
  </UModal>
</template>

<script>
import { VsButton, VsLoading } from "vuesax-ts";

export default {
  name: "modal",
  components: {
    VsButton,
    VsLoading
  },
  data() {
    return {
      isOpen: false,
      // The texts of a dialog opened without its own, in the person's language (lang/*.json, `modal`): set by `fire()`.
      title: "",
      content: "",
      confirmButtonText: "",
      cancelButtonText: "",
      loadings: [],
      loading: false,
      input: {
        label: "Input",
        type: "text",
        value: "",
        options: []
      },
      inputOptions: {},
      inputType: "text",
      confirmButtonColor: "success",
      inputValue: "",
      inputValidator: null,
      inputAttributes: {},
      hideCancelButton: false,
      html: null,
      pdf: null,
      preConfirm: null,
      closeOnSuccess: true,
      // Set by `fire()` only. As long as no modal has been opened,
      // there is nobody to notify: hence the `resolve?.()` guards.
      resolve: null,
      onKeyup: null,
      onDocumentClick: null
    };
  },
  mounted() {
    // These listeners are global: without the `isOpen` check, a simple
    // Escape key press on any page called `close()` on a modal that was never
    // opened, hence `this.resolve()` on `undefined`. That was the cause of the
    // `this.resolve is not a function` seen in production on six pages.
    this.onKeyup = (e) => {
      if (!this.isOpen) return;
      if (e.key === "Escape") this.close();
    };
    this.onDocumentClick = (e) => {
      if (!this.isOpen) return;
      if (e.target?.classList?.contains("modal")) this.close();
    };
    window.addEventListener("keyup", this.onKeyup);
    window.addEventListener("click", this.onDocumentClick);
  },
  unmounted() {
    if (this.onKeyup) window.removeEventListener("keyup", this.onKeyup);
    if (this.onDocumentClick) window.removeEventListener("click", this.onDocumentClick);
  },
  methods: {
    fire(data = {}) {
      this.isOpen = true;
      this.title = data.title ?? this.$t("modal.title");
      this.content = data.content ? data.content : data.text ? data.text : data.html ? null : this.$t("modal.content");
      this.confirmButtonText = data.confirmButtonText ?? this.$t("modal.confirm");
      this.cancelButtonText = data.cancelButtonText ?? this.$t("modal.cancel");
      this.input = data.input ?? null;
      this.html = data.html ?? null;
      this.preConfirm = data.preConfirm ?? null;
      this.didOpen = data.didOpen ?? null;
      this.loading = data.loading ?? false;
      this.confirmButtonColor = data.confirmButtonColor ?? "success";
      this.hideCancelButton = data.hideCancelButton ?? false;
      this.closeOnSuccess = data.closeOnSuccess ?? true;
      this.pdf = data.pdf ?? null;
      this.loadings = [];

      this.didOpen?.();
      return new Promise(resolve => {
        this.resolve = resolve;
      });
    },
    async confirm() {
      if (this.preConfirm) {
        this.loadings.push("confirm");
        const isConfirmed = (await this.preConfirm(this.input?.value)) ?? true;
        if (isConfirmed) {
          this.settle({
            isConfirmed: true,
            cancel: false
          });
          if (!this.closeOnSuccess) this.isOpen = false;
        } else this.loadings = this.loadings.filter(l => l !== "confirm");
      } else {
        this.settle({
          isConfirmed: true,
          cancel: false,
          value: this.input?.value
        });
        this.isOpen = false;
      }
    },
    hideLoading() {
      this.loading = false;
    },
    isLoading() {
      return this.loading;
    },
    showValidationMessage(message) {
      this.$refs.modalInput.errors.push(message);
    },
    cancel() {
      this.settle({
        isConfirmed: false,
        cancel: true
      });
      this.isOpen = false;
    },
    close() {
      this.settle({
        isConfirmed: false,
        dismiss: true
      });
      this.isOpen = false;
    },
    /**
     * Resolves the promise returned by `fire()`, at most once.
     * Without an open modal there is nothing to resolve, and a double click on
     * Confirm then Cancel must not resolve twice.
     */
    settle(result) {
      const resolve = this.resolve;
      this.resolve = null;
      resolve?.(result);
    }
  }
};
</script>
